import type {
  OddPlayerPolicy,
  PairingPriority,
  PlayEvent,
  PlayEventMatch,
  SocialPlayMode,
} from '../types/playEvent';
import { sortPlayEventMatchesForDisplay } from './playEventMatchDisplay';
import {
  type DrawMatchSlot,
  type DrawParticipant,
  generateRoundRobinRounds,
  generateSingleEliminationBracket,
  packRoundRobinIntoCourtRounds,
} from './playEventDraw';
import {
  type PairingMatch,
  type PairingPlayer,
  generateSocialRoundPairings,
  resolveSocialRoundMatchCount,
} from './playEventPairing';

export type DrawAllocationMode = 'auto' | 'manual';

export function resolveEffectiveSocialPlayMode(
  event: Pick<PlayEvent, 'side' | 'socialPlayMode'>
): SocialPlayMode {
  if (event.side === 'singles') {
    return 'singles_only';
  }
  if (event.side === 'doubles') {
    if (event.socialPlayMode === 'prefer_doubles') {
      return 'prefer_doubles';
    }
    return 'doubles_only';
  }
  return event.socialPlayMode ?? 'prefer_doubles';
}

export function resolvePlayEventMatchSideCapacity(
  event: Pick<PlayEvent, 'format' | 'side' | 'socialPlayMode'>
): number {
  return resolveEffectiveSocialPlayMode(event) === 'singles_only' ? 1 : 2;
}

export function resolvePlayEventMatchFormatLabel(
  event: Pick<PlayEvent, 'format' | 'side' | 'socialPlayMode'>
): 'Singles' | 'Doubles' {
  return resolvePlayEventMatchSideCapacity(event) >= 2 ? 'Doubles' : 'Singles';
}

export interface DrawPreviewParticipant extends DrawParticipant {
  gender?: 'male' | 'female' | 'other' | null;
  skillLevel?: string | null;
  dateOfBirth?: Date | null;
  prefersSingles?: boolean;
  displayName?: string;
}

export interface DrawPreviewMatch {
  roundNumber: number;
  matchIndex: number;
  sideAId?: string | null;
  sideBId?: string | null;
  /** Full side roster for doubles (and singles as a one-element array). */
  sideAIds?: string[];
  sideBIds?: string[];
  isBye?: boolean;
  result?: string | null;
  winnerSide?: 'a' | 'b' | null;
  scoreLabel?: string | null;
}

export function getDrawPreviewSideIds(match: DrawPreviewMatch, side: 'a' | 'b'): string[] {
  const sideIds = side === 'a' ? match.sideAIds : match.sideBIds;
  if (sideIds && sideIds.length > 0) {
    return sideIds;
  }
  const singleId = side === 'a' ? match.sideAId : match.sideBId;
  return singleId ? [singleId] : [];
}

export function previewMatchIncludesParticipant(
  match: DrawPreviewMatch,
  participantId: string
): boolean {
  return (
    getDrawPreviewSideIds(match, 'a').includes(participantId) ||
    getDrawPreviewSideIds(match, 'b').includes(participantId)
  );
}

export interface DrawPreviewRound {
  roundNumber: number;
  matches: DrawPreviewMatch[];
  sittingOut: string[];
}

export interface DrawPreview {
  rounds: DrawPreviewRound[];
  totalMatches: number;
  byeCount: number;
  unassignedParticipantIds: string[];
}

export interface BracketTreeSide {
  participantId?: string | null;
  label: string;
}

export interface BracketTreeMatch {
  matchIndex: number;
  roundNumber: number;
  sideA: BracketTreeSide;
  sideB: BracketTreeSide;
  feedsIntoMatchIndex?: number;
  feedsIntoSide?: 'a' | 'b';
  isBye?: boolean;
  result?: string | null;
  winnerSide?: 'a' | 'b' | null;
  scoreLabel?: string | null;
}

export interface BracketTreeRound {
  roundNumber: number;
  label: string;
  matches: BracketTreeMatch[];
}

export interface BracketTree {
  roundCount: number;
  rounds: BracketTreeRound[];
}

export interface SocialDrawPreviewInput {
  participants: DrawPreviewParticipant[];
  roundCount: number;
  socialPlayMode: SocialPlayMode;
  oddPlayerPolicy: OddPlayerPolicy;
  side: 'singles' | 'doubles' | 'mixed';
  pairingPriorities: PairingPriority[];
  courtCount: number;
  allocationMode: DrawAllocationMode;
  /** Non-zero values shuffle player order to produce alternate auto draws. */
  pairingShuffleSeed?: number;
}

export interface RoundRobinDrawPreviewInput {
  participantIds: string[];
  courtCount: number;
  allocationMode: DrawAllocationMode;
  pairingShuffleSeed?: number;
}

export interface SingleEliminationDrawPreviewInput {
  participants: DrawPreviewParticipant[];
  allocationMode: DrawAllocationMode;
  seedingMode?: 'auto' | 'manual';
  pairingShuffleSeed?: number;
}

/** Deterministic Fisher–Yates shuffle for reproducible alternate auto draws. */
export function seededShuffle<T>(items: readonly T[], seed: number): T[] {
  if (seed <= 0 || items.length < 2) {
    return [...items];
  }

  const copy = [...items];
  let state = seed >>> 0;
  const random = () => {
    state = (Math.imul(1664525, state) + 1013904223) >>> 0;
    return state / 0x100000000;
  };

  for (let index = copy.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [copy[index], copy[swapIndex]] = [copy[swapIndex], copy[index]];
  }

  return copy;
}

function nextPowerOfTwo(n: number): number {
  let p = 1;
  while (p < n) p *= 2;
  return p;
}

function addPartnerLink(priorPartners: Map<string, Set<string>>, a: string, b: string): void {
  if (a === b) return;
  if (!priorPartners.has(a)) priorPartners.set(a, new Set());
  if (!priorPartners.has(b)) priorPartners.set(b, new Set());
  priorPartners.get(a)!.add(b);
  priorPartners.get(b)!.add(a);
}

function recordPairingPartners(
  priorPartners: Map<string, Set<string>>,
  pairing: PairingMatch
): void {
  const sideA = pairing.sideA.playerIds;
  const sideB = pairing.sideB.playerIds;
  if (sideA.length === 2) {
    addPartnerLink(priorPartners, sideA[0], sideA[1]);
  }
  if (sideB.length === 2) {
    addPartnerLink(priorPartners, sideB[0], sideB[1]);
  }
  if (sideA.length === 1 && sideB.length === 1) {
    addPartnerLink(priorPartners, sideA[0], sideB[0]);
  }
}

function pairingMatchToPreviewMatches(
  roundNumber: number,
  pairings: PairingMatch[],
  startMatchIndex = 0
): DrawPreviewMatch[] {
  const matches: DrawPreviewMatch[] = [];
  let matchIndex = startMatchIndex;
  for (const pairing of pairings) {
    const sideAIds = pairing.sideA.playerIds;
    const sideBIds = pairing.sideB.playerIds;
    matches.push({
      roundNumber,
      matchIndex: matchIndex++,
      sideAId: sideAIds[0] ?? null,
      sideBId: sideBIds[0] ?? null,
      sideAIds,
      sideBIds,
      isBye: false,
    });
  }
  return matches;
}

function emptySocialRoundMatches(
  roundNumber: number,
  playerCount: number,
  startMatchIndex = 0
): DrawPreviewMatch[] {
  const matchCount = resolveSocialRoundMatchCount(playerCount);
  return Array.from({ length: matchCount }, (_, index) => ({
    roundNumber,
    matchIndex: startMatchIndex + index,
    sideAId: null,
    sideBId: null,
    isBye: false,
  }));
}

function collectAssignedParticipantIds(rounds: DrawPreviewRound[]): Set<string> {
  const assigned = new Set<string>();
  for (const round of rounds) {
    for (const match of round.matches) {
      for (const participantId of getDrawPreviewSideIds(match, 'a')) {
        assigned.add(participantId);
      }
      for (const participantId of getDrawPreviewSideIds(match, 'b')) {
        assigned.add(participantId);
      }
    }
    for (const id of round.sittingOut) {
      assigned.add(id);
    }
  }
  return assigned;
}

function summarizePreview(
  rounds: DrawPreviewRound[],
  allParticipantIds: string[]
): DrawPreview {
  const totalMatches = rounds.reduce((sum, round) => sum + round.matches.length, 0);
  const byeCount = rounds.reduce(
    (sum, round) => sum + round.matches.filter((match) => match.isBye).length,
    0
  );
  const assigned = collectAssignedParticipantIds(rounds);
  const unassignedParticipantIds = allParticipantIds.filter((id) => !assigned.has(id));

  return {
    rounds,
    totalMatches,
    byeCount,
    unassignedParticipantIds,
  };
}

export function buildSocialDrawPreview(input: SocialDrawPreviewInput): DrawPreview {
  const { participants, roundCount, allocationMode } = input;
  const participantIds = participants.map((participant) => participant.id);
  if (participants.length < 2 || roundCount < 1) {
    return summarizePreview([], participantIds);
  }

  const priorPartners = new Map<string, Set<string>>();
  const rounds: DrawPreviewRound[] = [];
  let globalMatchIndex = 0;

  for (let roundNumber = 1; roundNumber <= roundCount; roundNumber += 1) {
    if (allocationMode === 'manual') {
      const matches = emptySocialRoundMatches(roundNumber, participants.length, globalMatchIndex);
      globalMatchIndex += matches.length;
      rounds.push({
        roundNumber,
        matches,
        sittingOut: [],
      });
      continue;
    }

    const roundParticipants =
      allocationMode === 'auto' && input.pairingShuffleSeed
        ? seededShuffle(participants, input.pairingShuffleSeed + roundNumber)
        : participants;

    const players: PairingPlayer[] = roundParticipants.map((participant) => ({
      id: participant.id,
      gender: participant.gender,
      skillLevel: participant.skillLevel,
      playerRating: participant.playerRating,
      dateOfBirth: participant.dateOfBirth,
      priorPartnerIds: [...(priorPartners.get(participant.id) ?? [])],
      prefersSingles: participant.prefersSingles,
    }));

    const pairings = generateSocialRoundPairings({
      players,
      socialPlayMode: input.socialPlayMode,
      oddPlayerPolicy: input.oddPlayerPolicy,
      side: input.side,
      pairingPriorities: input.pairingPriorities,
      courtCount: input.courtCount,
    });

    for (const pairing of pairings) {
      recordPairingPartners(priorPartners, pairing);
    }

    const sittingOut = pairings[0]?.sittingOut ?? [];
    const matches = pairingMatchToPreviewMatches(roundNumber, pairings, globalMatchIndex);
    globalMatchIndex += matches.length;

    rounds.push({
      roundNumber,
      matches,
      sittingOut,
    });
  }

  return summarizePreview(rounds, participantIds);
}

export function computeRoundRobinRoundCount(participantCount: number): number {
  if (participantCount < 2) return 0;
  const pairRounds = generateRoundRobinRounds(
    Array.from({ length: participantCount }, (_, index) => `p${index}`)
  );
  return pairRounds.length;
}

export function computeRoundRobinScheduleRoundCount(
  participantCount: number,
  courtCount: number
): number {
  if (participantCount < 2 || courtCount < 1) return 0;
  const ids = Array.from({ length: participantCount }, (_, index) => `p${index}`);
  const pairRounds = generateRoundRobinRounds(ids);
  const packed = packRoundRobinIntoCourtRounds(pairRounds, courtCount);
  return packed.length;
}

export function computeSingleEliminationRoundCount(participantCount: number): number {
  if (participantCount < 1) return 0;
  const bracketSize = nextPowerOfTwo(participantCount);
  return Math.log2(bracketSize);
}

export function buildRoundRobinDrawPreview(input: RoundRobinDrawPreviewInput): DrawPreview {
  const { courtCount, allocationMode } = input;
  const participantIds =
    input.pairingShuffleSeed && input.pairingShuffleSeed > 0
      ? seededShuffle(input.participantIds, input.pairingShuffleSeed)
      : input.participantIds;
  if (participantIds.length < 2 || courtCount < 1) {
    return summarizePreview([], participantIds);
  }

  const pairRounds = generateRoundRobinRounds(participantIds);
  const packed = packRoundRobinIntoCourtRounds(pairRounds, courtCount);
  let globalMatchIndex = 0;

  const rounds: DrawPreviewRound[] = packed.map((packedRound) => {
    const matches: DrawPreviewMatch[] = packedRound.pairs.map(([sideAId, sideBId]) => {
      const match: DrawPreviewMatch = {
        roundNumber: packedRound.scheduleRound,
        matchIndex: globalMatchIndex++,
        sideAId: allocationMode === 'manual' ? null : sideAId,
        sideBId: allocationMode === 'manual' ? null : sideBId,
        isBye: false,
      };
      return match;
    });

    return {
      roundNumber: packedRound.scheduleRound,
      matches,
      sittingOut: [],
    };
  });

  return summarizePreview(rounds, participantIds);
}

export function buildSingleEliminationDrawPreview(
  input: SingleEliminationDrawPreviewInput
): DrawPreview {
  const { allocationMode } = input;
  const participants =
    input.pairingShuffleSeed && input.pairingShuffleSeed > 0
      ? seededShuffle(input.participants, input.pairingShuffleSeed)
      : input.participants;
  const participantIds = participants.map((participant) => participant.id);
  if (participants.length < 1) {
    return summarizePreview([], participantIds);
  }

  const drawParticipants =
    allocationMode === 'manual' && input.seedingMode === 'manual'
      ? participants.map((participant) => ({ id: participant.id, seed: participant.seed ?? null }))
      : participants;

  const bracket = generateSingleEliminationBracket(drawParticipants);
  const slots: DrawMatchSlot[] =
    allocationMode === 'manual'
      ? bracket.map((slot) => ({
          ...slot,
          sideAId: slot.roundNumber === 1 ? null : slot.sideAId,
          sideBId: slot.roundNumber === 1 ? null : slot.sideBId,
          isBye: false,
        }))
      : bracket;

  const roundsByNumber = new Map<number, DrawPreviewMatch[]>();
  for (const slot of slots) {
    const existing = roundsByNumber.get(slot.roundNumber) ?? [];
    existing.push({
      roundNumber: slot.roundNumber,
      matchIndex: slot.matchIndex,
      sideAId: slot.sideAId ?? null,
      sideBId: slot.sideBId ?? null,
      isBye: slot.isBye ?? false,
    });
    roundsByNumber.set(slot.roundNumber, existing);
  }

  const rounds: DrawPreviewRound[] = [...roundsByNumber.entries()]
    .sort(([a], [b]) => a - b)
    .map(([roundNumber, matches]) => ({
      roundNumber,
      matches: matches.sort((a, b) => a.matchIndex - b.matchIndex),
      sittingOut: [],
    }));

  return summarizePreview(rounds, participantIds);
}

function bracketRoundLabel(roundNumber: number, roundCount: number): string {
  const roundsFromFinal = roundCount - roundNumber;
  if (roundsFromFinal === 0) return 'Final';
  if (roundsFromFinal === 1) return 'Semi-final';
  if (roundsFromFinal === 2) return 'Quarter-final';
  return `Round ${roundNumber}`;
}

export interface SingleEliminationMatchDisplay {
  matchLabel: string;
  sideAPlaceholder?: string;
  sideBPlaceholder?: string;
  sideAIsFeeder: boolean;
  sideBIsFeeder: boolean;
}

export function getSingleEliminationGlobalMatchIndex(
  roundNumber: number,
  indexInRound: number,
  roundCount: number
): number {
  const bracketSize = 1 << roundCount;
  let matchesBeforeRound = 0;
  for (let round = 1; round < roundNumber; round += 1) {
    matchesBeforeRound += bracketSize >> round;
  }
  return matchesBeforeRound + indexInRound;
}

/** Global bracket match numbers (M1, M2, …) and feeder labels for later rounds. */
export function getSingleEliminationMatchDisplay(
  roundNumber: number,
  globalMatchIndex: number,
  indexInRound: number,
  roundCount: number,
  match: Pick<DrawPreviewMatch, 'sideAId' | 'sideBId' | 'isBye'>,
  participantNamesById?: Record<string, string>
): SingleEliminationMatchDisplay {
  const matchLabel = `M${globalMatchIndex + 1}`;

  const nameFor = (participantId?: string | null): string | undefined => {
    if (!participantId) return undefined;
    return participantNamesById?.[participantId] ?? undefined;
  };

  if (roundNumber <= 1) {
    return {
      matchLabel,
      sideAIsFeeder: false,
      sideBIsFeeder: false,
    };
  }

  const bracketSize = 1 << roundCount;
  let matchesBeforeRound = 0;
  for (let round = 1; round < roundNumber; round += 1) {
    matchesBeforeRound += bracketSize >> round;
  }
  const priorRoundMatchCount = bracketSize >> (roundNumber - 1);
  const priorRoundStartIndex = matchesBeforeRound - priorRoundMatchCount;
  const feederAIndex = priorRoundStartIndex + indexInRound * 2;
  const feederBIndex = feederAIndex + 1;

  const sideAName = nameFor(match.sideAId);
  const sideBName = nameFor(match.sideBId);

  return {
    matchLabel,
    sideAPlaceholder: sideAName ?? `Winner of M${feederAIndex + 1}`,
    sideBPlaceholder: sideBName ?? `Winner of M${feederBIndex + 1}`,
    sideAIsFeeder: !match.sideAId,
    sideBIsFeeder: !match.sideBId,
  };
}

export function buildBracketTree(
  matches: DrawPreviewMatch[],
  participantNamesById?: Record<string, string>
): BracketTree {
  if (matches.length === 0) {
    return { roundCount: 0, rounds: [] };
  }

  const roundNumbers = [...new Set(matches.map((match) => match.roundNumber))].sort((a, b) => a - b);
  const roundCount = roundNumbers.length;
  const matchesByRound = new Map<number, DrawPreviewMatch[]>();
  for (const match of matches) {
    const existing = matchesByRound.get(match.roundNumber) ?? [];
    existing.push(match);
    matchesByRound.set(match.roundNumber, existing);
  }

  const rounds: BracketTreeRound[] = roundNumbers.map((roundNumber) => {
    const roundMatches = (matchesByRound.get(roundNumber) ?? []).sort(
      (a, b) => a.matchIndex - b.matchIndex
    );
    const matchIndexOffset = roundMatches[0]?.matchIndex ?? 0;

    const treeMatches: BracketTreeMatch[] = roundMatches.map((match, indexInRound) => {
      const labelFor = (participantId?: string | null, fallback?: string): string => {
        if (participantId && participantNamesById?.[participantId]) {
          return participantNamesById[participantId];
        }
        if (participantId) return participantId;
        return fallback ?? 'TBD';
      };

      let sideA: BracketTreeSide;
      let sideB: BracketTreeSide;

      const elimDisplay = getSingleEliminationMatchDisplay(
        roundNumber,
        match.matchIndex,
        indexInRound,
        roundCount,
        match,
        participantNamesById
      );

      if (roundNumber === 1) {
        sideA = {
          participantId: match.sideAId,
          label: match.isBye && match.sideAId
            ? labelFor(match.sideAId)
            : labelFor(match.sideAId, 'TBD'),
        };
        sideB = {
          participantId: match.sideBId,
          label: match.isBye && !match.sideAId
            ? 'Bye'
            : labelFor(match.sideBId, match.isBye ? 'Bye' : 'TBD'),
        };
      } else {
        sideA = {
          participantId: match.sideAId,
          label: elimDisplay.sideAPlaceholder ?? 'TBD',
        };
        sideB = {
          participantId: match.sideBId,
          label: elimDisplay.sideBPlaceholder ?? 'TBD',
        };
      }

      const feedsIntoMatchIndex =
        roundNumber < roundCount
          ? matchIndexOffset + Math.floor(indexInRound / 2) + Math.pow(2, roundNumber - 1)
          : undefined;

      return {
        matchIndex: match.matchIndex,
        roundNumber,
        sideA,
        sideB,
        feedsIntoMatchIndex,
        feedsIntoSide: indexInRound % 2 === 0 ? 'a' : 'b',
        isBye: match.isBye ?? false,
        result: match.result ?? null,
        winnerSide: match.winnerSide ?? null,
        scoreLabel: match.scoreLabel ?? null,
      };
    });

    return {
      roundNumber,
      label: bracketRoundLabel(roundNumber, roundCount),
      matches: treeMatches,
    };
  });

  return { roundCount, rounds };
}

export function flattenDrawPreviewMatches(preview: DrawPreview): DrawPreviewMatch[] {
  return preview.rounds.flatMap((round) => round.matches);
}

/** Map persisted play-event matches into global bracket indices for tree display. */
export type PlayEventMatchBracketSource = Pick<
  PlayEventMatch,
  | 'id'
  | 'roundNumber'
  | 'matchIndex'
  | 'sideAParticipantId'
  | 'sideBParticipantId'
  | 'result'
  | 'winnerSide'
  | 'score'
> &
  Partial<Pick<PlayEventMatch, 'createdAt'>>;

export function buildDrawPreviewMatchesFromPlayEventMatches(
  matches: PlayEventMatchBracketSource[]
): DrawPreviewMatch[] {
  const byRound = new Map<number, typeof matches>();
  for (const match of matches) {
    const roundMatches = byRound.get(match.roundNumber) ?? [];
    roundMatches.push(match);
    byRound.set(match.roundNumber, roundMatches);
  }

  let globalMatchIndex = 0;
  const previewMatches: DrawPreviewMatch[] = [];
  const roundNumbers = [...byRound.keys()].sort((a, b) => a - b);

  for (const roundNumber of roundNumbers) {
    const roundMatches = sortPlayEventMatchesForDisplay(
      [...(byRound.get(roundNumber) ?? [])] as PlayEventMatch[]
    );
    for (const match of roundMatches) {
      const scoreLabel = match.score?.sets?.length
        ? match.score.sets.map((set) => `${set.a}-${set.b}`).join(', ')
        : null;

      previewMatches.push({
        roundNumber,
        matchIndex: globalMatchIndex++,
        sideAId: match.sideAParticipantId ?? null,
        sideBId: match.sideBParticipantId ?? null,
        isBye: match.result === 'bye',
        result: match.result ?? null,
        winnerSide: match.winnerSide ?? null,
        scoreLabel,
      });
    }
  }

  return previewMatches;
}
